import { addDays, addMonths, differenceInCalendarDays, differenceInMonths, subDays } from 'date-fns'

const Cycles = ['daily', 'weekly', 'monthly', 'quarterly'] as const
export type Cycle = (typeof Cycles)[number]
/**
 * Returns a new end date, based on the requested date range and the cycle.
 *
 * It will adjust the end date to the next date that matches the end of the cycle.
 * The end date is always inclusive, so we're subbing 1 day in some calculations.
 *
 * E.g. if the cycle is monthly, and dates are 1st and 5th of January, the new end date will be 31st of January.
 *
 * Each cycle works in a different way:
 * - daily: No adjustment is made, as dates are already days
 * - weekly: The end date is adjusted to match a cycle of 7 days
 * - monthly: The end date is adjusted to match a full month, whatever the days that month have
 * - quarterly: Same as monthly, but 3 months instead
 */
const adjustEndDateToCycle = (startDate: Date, endDate: Date, cycle: Cycle): Date => {
  switch (cycle) {
    case 'daily':
      return endDate

    case 'weekly': {
      const daysDiff = Math.max(differenceInCalendarDays(endDate, startDate) + 1, 1)
      const fullWeeks = Math.ceil(daysDiff / 7)

      return addDays(startDate, fullWeeks * 7 - 1)
    }

    case 'monthly': {
      const monthsDiff = differenceInMonths(endDate, startDate)
      const monthsDiffDate = addMonths(startDate, monthsDiff)
      // `differenceInMonths` returns the full months, so adding another if it's before the end date
      const adjustedMonthsDiffDate =
        monthsDiffDate < endDate ? addMonths(monthsDiffDate, 1) : monthsDiffDate

      return subDays(adjustedMonthsDiffDate, 1)
    }

    case 'quarterly': {
      const monthsDiff = differenceInMonths(endDate, startDate)
      const monthsDiffDate = addMonths(startDate, monthsDiff)
      const adjustedMonthsDiff = monthsDiffDate < endDate ? monthsDiff + 1 : monthsDiff
      const fullQuarters = Math.ceil(adjustedMonthsDiff / 3)

      return subDays(addMonths(startDate, fullQuarters * 3), 1)
    }
  }
}

/**
 * Adjusts the ranges to not surpass some limits:
 * - For days and weeks, 1 year
 * - For months and quarters, 3 years
 */
const adjustEndDateToMaxRange = (startDate: Date, endDate: Date, cycle: Cycle): Date => {
  switch (cycle) {
    case 'daily':
    case 'weekly': {
      if (differenceInMonths(endDate, startDate) >= 12) {
        return subDays(addMonths(startDate, 12), 1)
      }
      break
    }

    case 'monthly':
    case 'quarterly': {
      if (differenceInMonths(endDate, startDate) >= 36) {
        return subDays(addMonths(startDate, 36), 1)
      }
      break
    }
  }

  return endDate
}

export const calculateAdjustedEndDateForCycle = (
  startDate: Date,
  endDate: Date,
  cycle: Cycle
): Date => {
  const maxRangeAdjustedEndDate = adjustEndDateToMaxRange(startDate, endDate, cycle)

  return adjustEndDateToCycle(startDate, maxRangeAdjustedEndDate, cycle)
}
